import React, { useState, useEffect } from 'react';

interface FooterLink {
  label: string;
  href: string;
}

interface SocialLink {
  name: string;
  href: string;
  icon: string;
}

const Footer: React.FC = () => {
  const [email, setEmail] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);

  const companyLinks: FooterLink[] = [
    { label: 'About Us', href: '#about' },
    { label: 'Our Services', href: '#services' },
    { label: 'Portfolio', href: '#portfolio' },
    { label: 'Our Process', href: '#process' },
    { label: 'Testimonials', href: '#testimonials' },
    { label: 'Contact', href: '#contact' }
  ];

  const serviceLinks: FooterLink[] = [
    { label: 'E-commerce Development', href: '#services' },
    { label: 'Mobile App Development', href: '#services' },
    { label: 'Web Application', href: '#services' },
    { label: 'AI Chatbots', href: '#services' },
    { label: 'Digital Marketing', href: '#services' },
    { label: 'Custom Software', href: '#services' }
  ];

  const socialLinks: SocialLink[] = [
    { 
      name: 'LinkedIn', 
      href: '#', 
      icon: 'linkedin.png'
    },
    { 
      name: 'Twitter', 
      href: '#', 
      icon: 'twitter.png'
    },
    { 
      name: 'GitHub', 
      href: '#', 
      icon: 'github.png'
    },
    { 
      name: 'Instagram', 
      href: '#', 
      icon: 'instagram.png'
    },
    { 
      name: 'Facebook', 
      href: '#', 
      icon: 'facebook.png'
    },
    { 
      name: 'YouTube', 
      href: '#', 
      icon: 'youtube.png'
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  return (
    <footer className="dark:bg-navy-light bg-slate-200 dark:border-t dark:border-gray-700 border-t light:border-gray-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <h3 className="text-2xl font-bold dark:text-white text-gray-900 mb-4">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-light">
                  TechFlow
                </span>
              </h3>
              <p className="dark:text-gray-300 light:text-gray-600 leading-relaxed mb-6">
                We create innovative software solutions that transform businesses and drive growth. 
                Your success is our mission.
              </p>
            </div>

          </div>

          {/* Quick Links */}
          <div>
            <h4 className="dark:text-white light:text-gray-900 font-semibold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {companyLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="dark:text-gray-300 light:text-gray-600 hover:text-accent-light transition-colors duration-300 text-sm"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="dark:text-white light:text-gray-900 font-semibold mb-6">Our Services</h4>
            <ul className="space-y-3">
              {serviceLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="dark:text-gray-300 light:text-gray-600 hover:text-accent-light transition-colors duration-300 text-sm"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact & Social */}
          <div>
            <h4 className="dark:text-white light:text-gray-900 font-semibold mb-6">Connect With Us</h4>
            
            {/* Contact Info */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center dark:text-gray-300 light:text-gray-600 text-sm">
                <span className="mr-3">📧</span>
                contact@jagathisolutions.com
              </div>
              <div className="flex items-center dark:text-gray-300 light:text-gray-600 text-sm">
                <span className="mr-3">📞</span>
                +91 8919192170
              </div>
              <div className="flex items-center dark:text-gray-300 light:text-gray-600 text-sm">
                <span className="mr-3">📍</span>
                T-Nagar, Rajahmundry
              </div>
            </div>

            {/* Social Links */}
            <div>
              <h5 className="dark:text-white light:text-gray-900 font-medium mb-4">Follow Us</h5>
              <div className="flex flex-wrap gap-3">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    className="w-10 h-10 glass rounded-lg flex items-center justify-center hover:bg-opacity-30 transition-all duration-300 group"
                    title={social.name}
                  >
                    <img 
                      src={`${process.env.PUBLIC_URL}/assets/social/${social.icon}`}
                      alt={social.name}
                      className="w-5 h-5 group-hover:scale-110 transition-transform duration-300"
                    />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 w-12 h-12 bg-accent hover:bg-accent-dark text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 z-50 group"
          aria-label="Back to top"
        >
          <svg 
            className="w-6 h-6 mx-auto group-hover:-translate-y-0.5 transition-transform duration-300" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      )}

    </footer>
  );
};

export default Footer;
